import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Package, ArrowRight, Lock, Mail } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const dest = location.state?.from?.pathname || "/dashboard";
      nav(dest, { replace: true });
    }
  }, [user, nav, location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      {/* Image column */}
      <div className="relative hidden md:block bg-[#0B1B3D]">
        <img
          src="https://images.pexels.com/photos/37912780/pexels-photo-37912780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940"
          alt="Bodega farmacéutica"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1B3D]/80 via-[#0B1B3D]/60 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#D95A40] flex items-center justify-center">
              <Package className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-2xl leading-none tracking-tighter">
                EXIS
              </div>
              <div className="font-display text-[10px] uppercase tracking-[0.35em] text-slate-300 mt-1">
                Pharma Chile
              </div>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D95A40] mb-4">
              Plataforma Cliente / ADM
            </div>
            <h1 className="font-display font-black text-4xl lg:text-5xl leading-[1.05] tracking-tighter mb-4 max-w-md">
              Gestión de DTEs y distribución logística por comuna.
            </h1>
            <p className="text-slate-300 text-sm max-w-md leading-relaxed">
              Sube facturas y guías de despacho en XML, distribuye por comuna,
              descarga el archivo SAP consolidado. Diseñado para la operación
              farmacéutica chilena.
            </p>
          </div>

          <div className="flex gap-8 pt-6 border-t border-white/10 text-[11px] uppercase tracking-[0.2em] text-slate-400">
            <div>SII Chile</div>
            <div>346 Comunas</div>
            <div>16 Regiones</div>
          </div>
        </div>
      </div>

      {/* Form column */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D95A40] mb-3">
              Acceso
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#0B1B3D] mb-2">
              Iniciar sesión
            </h2>
            <p className="text-sm text-slate-600">
              Ingresa tus credenciales para continuar.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="tu.correo@empresa.cl"
                  className="w-full pl-10 pr-3 py-3 bg-white border border-slate-300 focus:border-[#D95A40] focus:ring-2 focus:ring-[#D95A40]/20 focus:outline-none text-sm transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-3 py-3 bg-white border border-slate-300 focus:border-[#D95A40] focus:ring-2 focus:ring-[#D95A40]/20 focus:outline-none text-sm transition-colors"
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.15em] text-sm transition-colors"
            >
              {loading ? "Ingresando..." : "Entrar"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 font-bold">
              Credenciales de prueba
            </div>
            <div className="text-xs text-slate-600 space-y-1 font-mono">
              <div>
                <span className="text-[#D95A40] font-bold">Admin:</span>{" "}
                admin@exispharma.cl / Admin2026!
              </div>
              <div>
                <span className="text-[#D95A40] font-bold">Cliente:</span>{" "}
                cliente@parthenon.cl / Cliente2026!
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
